import React from 'react';
import { Box, Typography } from '@mui/material';

interface SectionHeaderProps {
  /** 작은 카테고리 라벨 (uppercase + tracking) */
  eyebrow?: string;
  /** 디스플레이 폰트로 렌더되는 메인 타이틀 */
  title: string;
  /** 타이틀 아래 한 줄 설명 (옵션) */
  subtitle?: string;
  /** 우측에 배치할 액션(버튼/세그먼트/연도 셀렉터 등) */
  action?: React.ReactNode;
  /** 헤더 자체의 크기 — 카드 안(card)이면 작게, 페이지 섹션(page)이면 크게 */
  size?: 'card' | 'page';
}

/**
 * 일기월장 디자인 시스템 — 섹션 헤더 표준 패턴
 *
 *   [eyebrow]
 *   [TITLE]                       [action]
 *   [subtitle]
 *
 * 카드 안 헤더와 페이지 섹션 헤더 모두 같은 패턴 사용.
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  eyebrow,
  title,
  subtitle,
  action,
  size = 'card',
}) => {
  // 40대 이상 가독성 — Wanted DS 원본(22/17) 대비 상향
  const titleSize = size === 'page' ? 24 : 19;
  const titleTracking = size === 'page' ? '-0.019em' : '-0.012em';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1.5,
        mb: size === 'page' ? 3 : 2,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {eyebrow && (
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--fg-tertiary)',
              letterSpacing: '0.031em',
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography
          component="h3"
          sx={{
            fontFamily: 'var(--font-display)',
            fontSize: titleSize,
            fontWeight: size === 'page' ? 800 : 700,
            letterSpacing: titleTracking,
            color: 'var(--fg-strong)',
            lineHeight: 1.3,
            m: 0,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 15,
              color: 'var(--fg-tertiary)',
              letterSpacing: '0.014em',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Box>
  );
};

export default SectionHeader;
